import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Z_INDEX } from '../constants/zIndex.js';

const OfflineToastContext = createContext(null);

export const useOfflineToast = () => {
  const context = useContext(OfflineToastContext);
  if (!context) {
    throw new Error('useOfflineToast must be used within OfflineToastProvider');
  }
  return context;
};

export const OfflineToastProvider = ({ children }) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const toastIdRef = useRef(null);

  /**
   * Afficher le toast hors connexion
   * Ne crée le toast qu'une seule fois pour éviter les doublons
   */
  const showOfflineToast = useCallback(() => {
    // Si le toast existe déjà, ne rien faire - il persiste naturellement
    if (toastIdRef.current !== null) {
      return;
    }

    // Créer le toast une seule fois - non fermable manuellement
    toastIdRef.current = toast.error(
      <div style={{ lineHeight: '1.5' }}>
        <div style={{ fontWeight: 500, marginBottom: '4px' }}>Connexion interrompue.</div>
        <div style={{ fontSize: '15px', marginBottom: '2px' }}>Activez le Wi-Fi ou les données mobiles.</div>
        <div style={{ fontSize: '14px', color: '#666' }}>Secura consomme peu de données</div>
      </div>,
      {
        duration: Infinity, // Persiste jusqu'à ce que la connexion revienne
        position: 'top-center', // Position fixe en haut de l'écran
        id: 'offline-toast-global',
        style: {
          background: '#fff',
          color: '#333',
          border: '1px solid #fee',
          borderRadius: '8px',
          padding: '14px 18px',
          maxWidth: '400px',
          minWidth: '300px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '16px',
          fontWeight: 500,
          zIndex: Z_INDEX.TOAST
        }
      }
    );
  }, []);

  /**
   * Fermer le toast automatiquement quand la connexion revient
   */
  const hideOfflineToast = useCallback(() => {
    if (toastIdRef.current !== null) {
      toast.dismiss('offline-toast-global');
      toastIdRef.current = null;
    }
  }, []);

  /**
   * Gérer le changement d'état réseau
   */
  const handleOnline = useCallback(() => {
    setIsOffline(false);
    hideOfflineToast();
  }, [hideOfflineToast]);

  const handleOffline = useCallback(() => {
    setIsOffline(true);
    showOfflineToast();
  }, [showOfflineToast]);

  // Écouter les événements réseau du navigateur
  useEffect(() => {
    // Vérifier l'état initial
    if (!navigator.onLine) {
      setIsOffline(true);
      // Afficher le toast si on est déjà offline
      if (toastIdRef.current === null) {
        toastIdRef.current = toast.error(
          <div style={{ lineHeight: '1.5' }}>
            <div style={{ fontWeight: 500, marginBottom: '4px' }}>Connexion interrompue.</div>
            <div style={{ fontSize: '13px', marginBottom: '2px' }}>Activez le Wi-Fi ou les données mobiles.</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Secura consomme peu de données</div>
          </div>,
          {
            duration: Infinity,
            position: 'top-center',
            id: 'offline-toast-global',
            style: {
              background: '#fff',
              color: '#333',
              border: '1px solid #fee',
              borderRadius: '8px',
              padding: '12px 16px',
              maxWidth: '400px',
              minWidth: '300px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '14px',
              fontWeight: 500,
              zIndex: Z_INDEX.TOAST
            }
          }
        );
      }
    }

    // Écouter les changements d'état réseau
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Nettoyage
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  const value = {
    isOffline
  };

  return (
    <OfflineToastContext.Provider value={value}>
      {children}
    </OfflineToastContext.Provider>
  );
};

